import {Component, OnInit, inject, DestroyRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule, ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {DataService} from '../../services/store/data.service';
import {PredictionRotaWeek} from '../../classes/models/PredictionRotaWeek';
import moment from 'moment';
import {StoreSummary} from '../../classes/models/store/StoreSummary';
import {DayOfWeek} from '../../classes/enums/DayOfWeek';
import {DateParsePipe} from '../../pipes/date-parse.pipe';
import {DateFormatPipe} from '../../pipes/date-format.pipe';
import {DayOfWeekParsePipe} from '../../pipes/day-of-week-parse.pipe';
import {StoreNavigationComponent} from '../../shared/components/store-navigation/store-navigation.component';
import {RotaPageSwitchComponent} from '../../shared/components/rota-page-switch/rota-page-switch.component';
import {FootfallWeekTotalsComponent} from './footfall-week-totals-chart/footfall-week-totals-chart.component';
import {FootfallWeekChartComponent} from './footfall-week-chart/footfall-week-chart.component';
import {takeUntil} from "rxjs/operators";
import {SHARED_IMPORTS} from "../../shared/standalone-imports";

/**
 * This component displays footfall predictions for each day in a week and totals of the week.
 */
@Component({
  selector: 'app-footfall-week',
  templateUrl: './footfall-week.component.html',
  styleUrls: ['./footfall-week.component.less'],
  imports: [SHARED_IMPORTS, RotaPageSwitchComponent, FootfallWeekTotalsComponent, FootfallWeekChartComponent]
})
export class FootfallWeekComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A week information about footfall prediction. */
  predictionWeek: PredictionRotaWeek;
  /** A previous week information about footfall prediction. */
  previousPredictionWeek: PredictionRotaWeek;
  /** A next week information about footfall prediction. */
  nextPredictionWeek: PredictionRotaWeek;
  /** A store, for which footfall predictions are displayed. */
  selectedStore: StoreSummary;
  /** A list of each day of week. */
  weekdays: DayOfWeek[] = Object.values(DayOfWeek);

  /**
   * Finds current, previous and next prediction weeks on page initialization.
   */
  ngOnInit(): void {
    this.route.params
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                const id = this.route.snapshot.paramMap.get('weekId');
                if (this.dataExists()) {
                  this.selectedStore = this.dataService.selectedStore;
                  this.predictionWeek = this.getPredictionWeek(id);
                  this.getPreviousPredictionsWeek(this.predictionWeek.from);
                  this.getNextPredictionsWeek(this.predictionWeek.to);
                } else {
                  this.router.navigateByUrl('/');
                }
              }
            });
  }

  private dataExists(): boolean {
    return this.dataService.predictionRotaWeeks && this.dataService.predictionRotaWeeks.length > 0;
  }

  private getPredictionWeek(id): PredictionRotaWeek {
    return this.dataService.predictionRotaWeeks.find(predictionWeek => predictionWeek.id === id);
  }

  private getPreviousPredictionsWeek(date) {
    this.previousPredictionWeek = this.getPredictionWeek(moment(date).add(-1, 'weeks').format(PredictionRotaWeek.WEEK_FORMAT));
  }

  private getNextPredictionsWeek(date) {
    this.nextPredictionWeek = this.getPredictionWeek(moment(date).add(1, 'weeks').format(PredictionRotaWeek.WEEK_FORMAT));
  }

  /**
   * Scrolls to the selected id attribute in an HTML element.
   * @param id an id attribute of an HTML element.
   */
  scroll(id: string) {
    const itemToScrollTo = document.getElementById(id);
    itemToScrollTo.scrollIntoView();
  }
}
